/**
 * Theme configuration for validated components
 * This allows reusing existing styles while providing flexibility
 */

export const defaultFormStyles = {
  // Container styles
  container: {
    classes: 'w-full grid grid-cols-2 items-center gap-1',
    customClasses: ''
  },

  // Label styles
  label: {
    classes: '',
    styles: 'color: var(--label);',
    customClasses: '',
    customStyles: ''
  },

  // Input/Select/Textarea base styles
  input: {
    classes: 'rounded-md p-1 text-black placeholder-black',
    styles: {
      normal: {
        backgroundColor: 'var(--label)',
        color: 'var(--text)'
      },
      error: {
        backgroundColor: 'var(--obrigatorio)',
        color: 'var(--text)'
      },
      success: {
        backgroundColor: 'var(--label)',
        color: 'var(--text)'
      },
      disabled: {
        backgroundColor: '#f3f4f6',
        color: '#9ca3af',
        cursor: 'not-allowed',
        opacity: '0.6'
      }
    },
    customClasses: '',
    customStyles: ''
  },

  // Select specific styles
  select: {
    classes: 'rounded-md p-1 text-black placeholder-gray-500',
    styles: {
      normal: {
        backgroundColor: 'var(--label)',
        color: 'var(--text)'
      },
      error: {
        backgroundColor: 'var(--obrigatorio)',
        color: 'var(--text)'
      },
      success: {
        backgroundColor: 'var(--label)',
        color: 'var(--text)'
      },
      disabled: {
        backgroundColor: '#f3f4f6',
        color: '#9ca3af',
        cursor: 'not-allowed',
        opacity: '0.6'
      }
    },
    customClasses: '',
    customStyles: ''
  },

  // Textarea specific styles
  textarea: {
    classes: 'rounded-md p-2 text-black placeholder-black',
    styles: {
      normal: {
        backgroundColor: 'var(--label)',
        color: 'var(--text)'
      },
      error: {
        backgroundColor: 'var(--obrigatorio)',
        color: 'var(--text)'
      },
      success: {
        backgroundColor: 'var(--label)',
        color: 'var(--text)'
      },
      disabled: {
        backgroundColor: '#f3f4f6',
        color: '#9ca3af',
        cursor: 'not-allowed',
        opacity: '0.6'
      }
    },
    customClasses: '',
    customStyles: ''
  },

  // Error message styles
  errorMessage: {
    classes: 'text-xs text-red-500 mt-1',
    styles: 'color: var(--vermelho-2, #FD5D5D)',
    customClasses: '',
    customStyles: '',
    position: 'below' // 'below', 'absolute', 'none'
  },

  // Success indicator styles
  successIndicator: {
    classes: 'text-green-500',
    styles: 'color: var(--verde-claro, #28a745);',
    show: false // Disable by default to match existing behavior
  },

  // Loading indicator styles
  loadingIndicator: {
    classes: 'text-blue-500',
    styles: 'color: var(--azul, #006698);',
    show: true
  }
};

/**
 * Merge custom styles with default styles
 * @param {Object} customStyles - Custom style overrides
 * @returns {Object} Merged style configuration
 */
export function mergeStyles(customStyles = {}) {
  return {
    container: {
      ...defaultFormStyles.container,
      ...customStyles.container
    },
    label: {
      ...defaultFormStyles.label,
      ...customStyles.label
    },
    input: {
      ...defaultFormStyles.input,
      styles: {
        ...defaultFormStyles.input.styles,
        ...customStyles.input?.styles
      },
      ...customStyles.input
    },
    select: {
      ...defaultFormStyles.select,
      styles: {
        ...defaultFormStyles.select.styles,
        ...customStyles.select?.styles
      },
      ...customStyles.select
    },
    textarea: {
      ...defaultFormStyles.textarea,
      styles: {
        ...defaultFormStyles.textarea.styles,
        ...customStyles.textarea?.styles
      },
      ...customStyles.textarea
    },
    errorMessage: {
      ...defaultFormStyles.errorMessage,
      ...customStyles.errorMessage
    },
    successIndicator: {
      ...defaultFormStyles.successIndicator,
      ...customStyles.successIndicator
    },
    loadingIndicator: {
      ...defaultFormStyles.loadingIndicator,
      ...customStyles.loadingIndicator
    },
    // Add support for wrapper styles
    inputWrapper: customStyles.inputWrapper,
    selectWrapper: customStyles.selectWrapper,
    textareaWrapper: customStyles.textareaWrapper
  };
}

/**
 * Get computed styles for a field state
 * @param {string} fieldType - 'input', 'select', or 'textarea'
 * @param {string} state - 'normal', 'error', or 'success'
 * @param {Object} styleConfig - Style configuration
 * @returns {Object} Computed styles
 */
export function getFieldStyles(fieldType, state, styleConfig = defaultFormStyles) {
  const fieldStyles = styleConfig[fieldType];
  if (!fieldStyles) return {};

  const baseStyles = fieldStyles.styles[state] || fieldStyles.styles.normal;
  const customStyles = fieldStyles.customStyles || {};

  return {
    ...baseStyles,
    ...customStyles
  };
}

/**
 * Get computed classes for a field
 * @param {string} fieldType - 'input', 'select', or 'textarea'
 * @param {Object} styleConfig - Style configuration
 * @returns {string} Computed classes
 */
export function getFieldClasses(fieldType, styleConfig = defaultFormStyles) {
  const fieldStyles = styleConfig[fieldType];
  if (!fieldStyles) return '';

  return `${fieldStyles.classes} ${fieldStyles.customClasses || ''}`.trim();
}

export default defaultFormStyles;